import { useEffect, useRef } from "react";
import { buildSolidModel, type ModelKind } from "./solidGeometry";

const vertexSource = `
  attribute vec3 a_position;
  attribute vec3 a_normal;
  attribute vec3 a_color;
  uniform vec3 u_angles;
  uniform float u_scale;
  uniform float u_aspect;
  varying vec3 v_normal;
  varying vec3 v_color;
  vec3 rotate(vec3 p) {
    vec3 c = cos(u_angles), s = sin(u_angles);
    p.yz = mat2(c.x, s.x, -s.x, c.x) * p.yz;
    p.xz = mat2(c.y, -s.y, s.y, c.y) * p.xz;
    p.xy = mat2(c.z, s.z, -s.z, c.z) * p.xy;
    return p;
  }
  void main() {
    vec3 p = rotate(a_position) * u_scale;
    float perspective = 4.0 / (4.0 - p.z);
    gl_Position = vec4(p.x * perspective / u_aspect, p.y * perspective, -p.z * 0.15, 1.0);
    v_normal = rotate(a_normal);
    v_color = a_color;
  }
`;

const fragmentSource = `
  precision mediump float;
  varying vec3 v_normal;
  varying vec3 v_color;
  void main() {
    vec3 normal = normalize(v_normal);
    float key = max(dot(normal, normalize(vec3(-0.6, 0.8, 1.4))), 0.0);
    float fill = max(dot(normal, normalize(vec3(0.8, 0.1, 0.6))), 0.0);
    float highlight = pow(max(dot(reflect(-normalize(vec3(-0.6, 0.8, 1.4)), normal), vec3(0.0, 0.0, 1.0)), 0.0), 28.0);
    gl_FragColor = vec4(v_color * (0.48 + key * 0.47 + fill * 0.13) + highlight * 0.065, 1.0);
  }
`;

const poses = {
  controller: { angles: [-0.28, -0.38, -0.24], scale: 0.44 },
  headphones: { angles: [-0.14, -0.45, 0.22], scale: 0.47 },
  bubble: { angles: [0.12, -0.4, 0.18], scale: 0.43 },
} as const;

function createRenderer(canvas: HTMLCanvasElement, kind: ModelKind) {
  const gl = canvas.getContext("webgl", { alpha: true, antialias: true, premultipliedAlpha: false });
  if (!gl) return;
  const shaders: WebGLShader[] = [];
  const program = gl.createProgram();
  const buffer = gl.createBuffer();
  if (!program || !buffer) return;
  const dispose = () => {
    gl.deleteBuffer(buffer);
    gl.deleteProgram(program);
    shaders.forEach((shader) => gl.deleteShader(shader));
  };
  for (const [type, source] of [[gl.VERTEX_SHADER, vertexSource], [gl.FRAGMENT_SHADER, fragmentSource]] as const) {
    const shader = gl.createShader(type);
    if (!shader) { dispose(); return; }
    shaders.push(shader);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) { dispose(); return; }
    gl.attachShader(program, shader);
  }
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) { dispose(); return; }
  gl.useProgram(program);
  const vertices = buildSolidModel(kind);
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
  for (const [i, name] of ["a_position", "a_normal", "a_color"].entries()) {
    const attribute = gl.getAttribLocation(program, name);
    gl.enableVertexAttribArray(attribute);
    gl.vertexAttribPointer(attribute, 3, gl.FLOAT, false, 36, i * 12);
  }
  const angles = gl.getUniformLocation(program, "u_angles");
  const scale = gl.getUniformLocation(program, "u_scale");
  const aspect = gl.getUniformLocation(program, "u_aspect");
  gl.enable(gl.DEPTH_TEST);
  gl.clearColor(0, 0, 0, 0);
  const pose = poses[kind];
  return {
    draw(scroll: number) {
      if (gl.isContextLost()) return;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (!width || !height) return;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const pixelWidth = Math.round(width * ratio);
      const pixelHeight = Math.round(height * ratio);
      if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
        canvas.width = pixelWidth;
        canvas.height = pixelHeight;
      }
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.uniform3f(angles, pose.angles[0] + Math.sin(scroll * 0.001) * 0.1, pose.angles[1] + Math.sin(scroll * 0.0015) * 0.2, pose.angles[2] + Math.sin(scroll * 0.0008) * 0.08);
      gl.uniform1f(scale, pose.scale);
      gl.uniform1f(aspect, width / height);
      gl.drawArrays(gl.TRIANGLES, 0, vertices.length / 9);
    },
    dispose,
  };
}

export function SolidModel({ kind, background = false }: { kind: ModelKind; background?: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || typeof window.WebGLRenderingContext === "undefined") return;
    const renderer = createRenderer(canvas, kind);
    if (!renderer) return;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    let visible = false;
    const draw = () => {
      frame = 0;
      if (!visible || document.hidden) return;
      if (background && canvas.parentElement) {
        const bounds = canvas.parentElement.getBoundingClientRect();
        const progress = Math.max(0, Math.min(1, (window.innerHeight - bounds.top) / (window.innerHeight + bounds.height)));
        const focus = Math.sin(progress * Math.PI);
        canvas.style.setProperty("--model-blur", `${motion.matches ? 3 : 1.5 + (1 - focus) * 4}px`);
        canvas.style.setProperty("--model-opacity", String(motion.matches ? 0.36 : 0.22 + focus * 0.32));
        canvas.style.setProperty("--model-parallax", `${motion.matches ? 0 : (0.5 - progress) * 70}px`);
      }
      renderer.draw(motion.matches ? 0 : window.scrollY);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(draw); };
    const onScroll = () => { if (!motion.matches && visible) schedule(); };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry?.isIntersecting ?? false;
      canvas.dataset.visible = String(visible);
      if (visible) schedule();
    });
    observer.observe(background && canvas.parentElement ? canvas.parentElement : canvas);
    const resize = new ResizeObserver(schedule);
    resize.observe(canvas);
    window.addEventListener("scroll", onScroll, { passive: true });
    motion.addEventListener("change", schedule);
    schedule();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      resize.disconnect();
      window.removeEventListener("scroll", onScroll);
      motion.removeEventListener("change", schedule);
      renderer.dispose();
    };
  }, [kind, background]);
  const model = <canvas ref={ref} className={`community-solid community-solid-${kind}`} aria-hidden="true" />;
  return background ? <div className={`community-background-model community-background-${kind}`} aria-hidden="true">{model}</div> : model;
}
